import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import ScannerInput from "src/components/form/ScannerInput";
import AssetItemImage from "src/components/capture/aseet-list/AssetItemImage";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import { ADD_INVENTORY_ITEM_MUTATION } from "src/constants/graphql/mutations";
import GroupedAssetSearchResults from "./GroupedAssetSearchResults";

export default function GroupedAssetScanModal({
  open,
  inventoryItem,
  projectId,
  inventoryRequestId,
  existingItems = [],
  onClose,
}) {
  const [addedItems, setAddedItems] = useState([]);
  const [searchCode, setSearchCode] = useState("");
  const searching = searchCode !== "";
  const remainingCount = inventoryItem.quantity - existingItems.length - addedItems.length;
  const allItems = [
    ...existingItems.map((item) => ({
      id: item.asset.id,
      itemId: item.asset.itemId,
      description: item.asset.description,
      serialNumber: item.asset.serialNumber,
      images: item.asset.images,
    })),
    ...addedItems,
  ];
  const formikRef = useRef(null);

  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION);
  const { notifySuccess, notifyError } = useNotification();

  useEffect(() => {
    if (remainingCount <= 0 && addedItems.length > 0) {
      onClose();
    }
  }, [remainingCount, onClose]);

  const handleAddItem = (asset) => {
    addInventoryItem({
      variables: {
        assetId: asset.id,
        inventoryRequestId,
        quantity: 1,
        projectId,
        pickedStatus: true,
      },
      onSuccess: () => {
        notifySuccess("Item added successfully");
        setAddedItems((prev) => [
          ...prev,
          {
            id: asset.id,
            itemId: asset.itemId,
            description: asset.description,
            serialNumber: asset.serialNumber,
            images: asset.images,
          },
        ]);
        setSearchCode("");
        if (formikRef.current) {
          formikRef.current.resetForm();
        }
      },
      onFailure: (errors) => {
        notifyError(errors.quantity || errors.asset || errors.submit || "Failed to add item");
      },
    });
  };

  const handleSearch = (values) => {
    if (values.code.trim()) {
      setSearchCode(values.code.trim());
    }
  };

  const handleDetected = (code) => {
    if (code.trim()) {
      setSearchCode(code.trim());
    }
  };

  const handleSearchAgain = () => {
    setSearchCode("");
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ position: "relative", px: 6, pt: 3 }}>
        <Typography variant="h5" color="text.primary" fontWeight={500} textAlign="center">
          Add {inventoryItem.quantity} {inventoryItem.asset?.description || "items"}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mt={0.5} fontWeight={500}>
          {remainingCount} more remaining
        </Typography>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 200, maxHeight: "70vh", overflow: "auto" }}>
        <Box pt={2} />
        <Formik innerRef={formikRef} initialValues={{ code: "" }} onSubmit={handleSearch}>
          {({ values }) => (
            <Form>
              <ScannerInput
                autoFocus
                size="large"
                name="code"
                label="Search"
                showNAButton={false}
                onDetected={handleDetected}
                scanContainerProps={{ justifyContent: "flex-start" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        sx={{
                          alignSelf: "stretch",
                          borderLeft: theme => `1px solid ${theme.palette.divider}`,
                          mr: 1,
                        }}
                      />
                      <IconButton type="submit" disabled={!values.code.trim()}>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Form>
          )}
        </Formik>

        {searching && searchCode && (
          <GroupedAssetSearchResults
            searchCode={searchCode}
            projectId={projectId}
            onAutoAdd={handleAddItem}
            onSelect={handleAddItem}
            onBack={handleSearchAgain}
          />
        )}

        {allItems.length > 0 && (
          <Box mt={3}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={1}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Items Added So Far ({allItems.length})
              </Typography>
            </Box>
            <List
              sx={{
                bgcolor: "success.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                py: 0,
              }}
            >
              {allItems.map((item, index) => (
                <React.Fragment key={item.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem sx={{ py: 1, px: 2 }}>
                    <ListItemAvatar sx={{ minWidth: 56 }}>
                      <AssetItemImage
                        images={item.images}
                        width={44}
                        minWidth={44}
                        height={44}
                        minHeight={44}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {item.itemId}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                          {item.serialNumber && (
                            <Typography variant="body2" display="block" color="text.secondary" mt={0.25}>
                              S/N: {item.serialNumber}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
